type AbortableFetcherOptions = {
    timeout?: number;
}

/**
 * Klasse zum ausführen von Fetch-Requests, die abgebrochen werden können.
 *
 * Der Abbruch kann manuell erfolgen oder automatisch nach einem Timeout.
 *
 * Achtung: Die Objekte dieser Klasse sind jeweils nur für EINEN Request
 * verwendbar. Für wiederholte Anfragen muss jeweils ein neues Objekt
 * erstellt werden.
 *
 * Neben einer generischen `fetch()`-Methode wird eine `fetchJson()`-Methode
 * bereitgestellt, die das Ergebnis als JSON parst.
 */
export default class AbortableFetcher
{
    url: string | Request;

    options: AbortableFetcherOptions;

    abortController?: AbortController;

    /**
     * @param url Eine string-bare Repräsentation der URL oder ein Request-Objekt.
     * @param options Konfigurationsoptionen für den Request. Unterstützte Optionen:
     *                - `timeout`: Ein optionaler Timeout in Millisekunden.
     *                  Falls kein Timeout angegeben wird, kann der Request nur manuell abgebrochen werden.
     */
    constructor(url: string|Request, options: AbortableFetcherOptions = {})
    {
        this.url = url;
        this.options = options;
    }

    /**
     * Führt den Request aus und gibt das Resultat zurück.
     *
     * Achtung: Für jede Instanz dieser Klasse darf diese Methode
     * (oder `fetchJson()`) nur einmal aufgerufen werden.
     *
     * @throws {DOMException} Wenn der Request abgebrochen wurde.
     *                        - Der `name` ist `AbortError`, wenn der Abbruch manuell getriggert wurde.
     *                        - Der `name` ist `TimeoutError`, wenn der Request aufgrund des Timeouts
     *                          abgebrochen wurde.
     * @throws {TypeError} Bei einem Netzwerkfehler oder einem anderen Problem.
     */
    fetch()
    {
        this.abortController = new AbortController();
        // TODO@modern_js:
        // This could be done more elegantly / robustly with AbortController::timeout
        // in combination with AbortSignal::any
        if (typeof this.options.timeout === 'number') {
            setTimeout(() => {
                this.abortController?.abort(new DOMException('Operation timed out', 'TimeoutError'));
            }, this.options.timeout);
        }
        return fetch(this.url, {signal: this.abortController.signal});
    }

    /**
     * Führt den Request aus und parst das Ergebnis als JSON.
     *
     * Achtung: Für jede Instanz dieser Klasse darf diese Methode
     * (oder `fetch()`) nur einmal aufgerufen werden.
     *
     * @throws {DOMException} Wenn der Request abgebrochen wurde oder der HTTP-Status nicht `200` war.
     * @throws {TypeError} Bei einem Netzwerkfehler oder einem anderen Problem.
     * @throws {SyntaxError} Wenn das Ergebnis nicht als JSON geparst werden kann.
     */
    async fetchJson()
    {
        const response = await this.fetch();
        if (!response.ok) {
            throw new DOMException(`HTTP status ${response.status}`, 'HttpStatusError');
        }
        return await response.json() as unknown;
    }

    /**
     * Bricht den Request ab.
     */
    abortRequest(): void
    {
        this.abortController?.abort();
    }
}